import { useState } from "react";
import PropTypes from "prop-types";
import { TabContent, TabPane, Row } from "reactstrap";
import { BenefitStates } from "v2/infrastructure/Benefits/types/BenefitStates";
import {
  SplitTreatments,
  withSplitFactory,
} from "@splitsoftware/splitio-react";
import { CountryIsoCode } from "v2/domain/common/enums/CountryISOCode";
import { BenefitTypeEnum } from "v2/domain/common/entities/BenefitType";
import { BenefitType } from "v2/infrastructure/Benefits/types/BenefitTypes";
import auth0Client from "../../auth/Auth";
import { translate as t } from "../../../infrastructure/translate/localization";
import {
  showFlexWarning,
  shouldHideTransport,
} from "../../../infrastructure/country-rules";

import BenefitCard from "./BenefitCard";

import accidentImg from "../../../assets/img/accidente.png";

import mealBenefitImageV2 from "../../../assets/img/v2/meal-benefit.png";
import mealBenefitDisabledImageV2 from "../../../assets/img/v2/meal-benefit-disabled.png";
import transportBenefitImageV2 from "../../../assets/img/v2/transport-benefit.png";
import transportBenefitDisabledImageV2 from "../../../assets/img/v2/transport-benefit-disabled.png";
import nurseryBenefitImageV2 from "../../../assets/img/v2/nursery-benefit.png";
import nurseryBenefitDisabledImageV2 from "../../../assets/img/v2/nursery-benefit-disabled.png";
import educationBenefitImageV2 from "../../../assets/img/v2/education-benefit.png";
import couponsBenefitImageV2 from "../../../assets/img/v2/coupons.png";
import couponsBenefitDisabledImageV2 from "../../../assets/img/v2/coupons-disabled.png";
import wellnessBenefitImageV2 from "../../../assets/img/v2/wellness-benefit.png";
import wellnessBenefitDisabledImageV2 from "../../../assets/img/v2/wellness-benefit-disabled.png";

import { displayMealsConfigModal } from "../../../usecases/benefit-model/displays/display-meals-config-modal";
import { displayGiftCardModal } from "../../../usecases/benefit-model/displays/display-benefit-gift-card-modal";
import configureNurseryBenefit from "../../../usecases/benefit-model/configureNurseryBenefit";
import configIFeelBenefit from "../../../usecases/benefit-model/configIFeelBenefit";
import "./benefitsTabs.scss";
import featureFlagsDictionary from "../../../infrastructure/split/feature-flags-dictionary";
import sdkConfig from "../../../infrastructure/split/sdkConfig";
import CobeeButton from "../CobeeButton";
import BenefitGym from "./BenefitGym/BenefitGym.tsx";
import PensionPlanTab from "./tabsByBenefit/PensionPlanTab";
import RentingTab from "./tabsByBenefit/RentingTab";
import LifeInsuranceTab from "./tabsByBenefit/LifeInsuranceTab";
import RetirementTab from "./tabsByBenefit/RetirementInsuranceTab";
import CustomTab from "./tabsByBenefit/CustomTab";
import { BenefitCategory } from "../../../v2/domain/common/entities/BenefitCagetory";
import analytics from "../../segment";
import events from "../../segment/events";
import HealthInsuranceCard from "./HealthInsuranceCard";
import alternateCountryTranslations from "../../../infrastructure/translate/alternateCountyTranslations";

function buildBenefitImage(imageUrl) {
  return `https://assets.cobee.io/${imageUrl}`;
}

function redirectTo(url) {
  window.location.assign(url);
}

const getCustomBenefitProps = ({ benefitProps, benefit, selectedTab }) => {
  return {
    ...benefitProps,
    image: buildBenefitImage(benefit.iconUrl),
    title: benefit.name,
    handleSeeMore: () => {
      analytics.track(events.benefitCard.name, {
        type: benefit.type,
        name: benefit.name,
        id: benefit.benefit,
        state: benefit.state,
      });
      redirectTo(
        `/v2/benefits/compensation-plan/${selectedTab.id}/custom-benefits/${benefit.benefit}`
      );
    },
  };
};

const BenefitsTabs = ({
  selectedTab,
  toggleActive,
  disableAccumulation,
  insuranceProducts,
  hasRenting,
  hasLifeInsurance,
  country,
  v2BenefitsConfig,
  companyGymBenefit,
  benefitsV2,
}) => {
  const [state, setState] = useState({
    companyId: auth0Client.getCompanyId(),
    accident: false,
    retirement: false,
    lifeInsurance: false,
  });

  const { accident, companyId } = state;

  const isCompanyDemo = companyId === "a0504993-b3a7-457f-8f42-20ed0b2620d4";

  const { retirementInsuranceBenefit, pensionPlanBenefit } = v2BenefitsConfig;

  const retirementInsuranceIsEnabledOnCompany = Boolean(
    retirementInsuranceBenefit?.enabled
  );
  const pensionPlanIsEnabledOnCompany = Boolean(pensionPlanBenefit?.enabled);

  return (
    <TabContent activeTab={selectedTab.id}>
      <TabPane tabId={selectedTab.id}>
        <div className="benefits-tabs">
          <p className="section-description">
            {t("benefits-section.current-model-benefit", {
              name: selectedTab.name,
            })}
          </p>
          <SplitTreatments
            names={[featureFlagsDictionary.BI_2458]}
            attributes={{
              companyId,
              countryISOCode: country,
              benefit: BenefitCategory.CUSTOM,
            }}
          >
            {({ treatments, isReady }) => {
              const isActiveTreatment =
                treatments[featureFlagsDictionary.BI_2458].treatment === "on";

              if (isReady && isActiveTreatment) {
                return (
                  <Row className="cards-container">
                    <CustomTab companyId={companyId} country={country} />
                  </Row>
                );
              }
              return null;
            }}
          </SplitTreatments>
          <Row className="cards-container">
            {country !== CountryIsoCode.MEXICO && (
              <>
                <SplitTreatments
                  names={[featureFlagsDictionary.BI_6]}
                  attributes={{ companyId, countryISOCode: country }}
                >
                  {({ treatments, isReady }) => {
                    const isActiveTreatment =
                      treatments[featureFlagsDictionary.BI_6].treatment ===
                      "on";

                    const mealProps =
                      isReady && isActiveTreatment
                        ? {
                            hasV2Screen: true,
                            handleSeeMore: () =>
                              redirectTo(
                                `/v2/benefits/compensation-plan/${
                                  selectedTab.id
                                }/meal?tab=${
                                  selectedTab.mealBenefit
                                    ? "settings"
                                    : "information"
                                }`
                              ),
                          }
                        : {
                            tooltipText: t(
                              "benefits-section.meal-benefit.meal-tooltip"
                            ),
                            showDisableFlexMessage:
                              showFlexWarning(country) ||
                              selectedTab.disabledFlex["meal-benefit"],
                            advanced: true,
                            handleAdvance: () =>
                              displayMealsConfigModal({
                                companyId,
                                benefitSelected: selectedTab,
                                country,
                              }),
                            handleActiveToggle: () => {
                              disableAccumulation(
                                selectedTab.id,
                                "mealBenefit",
                                "accumulationMealBenefit"
                              );
                            },
                          };

                    return (
                      <BenefitCard
                        benefit={BenefitTypeEnum.MEAL_BENEFIT}
                        title={t("benefits-section.meal-benefit.meal")}
                        active={selectedTab.mealBenefit}
                        image={
                          selectedTab.mealBenefit
                            ? mealBenefitImageV2
                            : mealBenefitDisabledImageV2
                        }
                        country={country}
                        {...mealProps}
                      />
                    );
                  }}
                </SplitTreatments>
                {!shouldHideTransport(country) && (
                  <SplitTreatments
                    names={[featureFlagsDictionary.BI_264]}
                    attributes={{
                      companyId,
                      countryISOCode: country,
                      benefit: "transport",
                    }}
                  >
                    {({ treatments, isReady }) => {
                      const isActiveTreatment =
                        treatments[featureFlagsDictionary.BI_264].treatment ===
                        "on";

                      const transportProps =
                        isReady && isActiveTreatment
                          ? {
                              benefit: BenefitTypeEnum.TRANSPORT_BENEFIT,
                              hasV2Screen: true,
                              handleSeeMore: () =>
                                redirectTo(
                                  `/v2/benefits/compensation-plan/${
                                    selectedTab.id
                                  }/transport?tab=${
                                    selectedTab.transportBenefit
                                      ? "settings"
                                      : "information"
                                  }`
                                ),
                            }
                          : {
                              benefit: BenefitTypeEnum.TRANSPORT_BENEFIT,
                              tooltipText: t(
                                "benefits-section.transport-benefit.transport-tooltip"
                              ),
                              showDisableFlexMessage:
                                showFlexWarning(country) ||
                                selectedTab.disabledFlex["transport-benefit"],
                              handleActiveToggle: () => {
                                disableAccumulation(
                                  selectedTab.id,
                                  "transportBenefit",
                                  "accumulationTransportBenefit"
                                );
                              },
                            };

                      return (
                        <BenefitCard
                          benefit={BenefitTypeEnum.TRANSPORT}
                          title={t(
                            "benefits-section.transport-benefit.transport"
                          )}
                          tooltipText={t(
                            "benefits-section.transport-benefit.transport-tooltip"
                          )}
                          active={selectedTab.transportBenefit}
                          image={
                            selectedTab.transportBenefit
                              ? transportBenefitImageV2
                              : transportBenefitDisabledImageV2
                          }
                          country={country}
                          {...transportProps}
                        />
                      );
                    }}
                  </SplitTreatments>
                )}
                <SplitTreatments
                  names={[featureFlagsDictionary.BI_264]}
                  attributes={{
                    companyId,
                    countryISOCode: country,
                    benefit: "nursery",
                  }}
                >
                  {({ treatments, isReady }) => {
                    const isActiveTreatment =
                      treatments[featureFlagsDictionary.BI_264].treatment ===
                      "on";

                    const nurseryProps =
                      isReady && isActiveTreatment
                        ? {
                            hasV2Screen: true,
                            handleSeeMore: () =>
                              redirectTo(
                                `/v2/benefits/compensation-plan/${
                                  selectedTab.id
                                }/nursery?tab=${
                                  selectedTab.nurseryBenefit
                                    ? "settings"
                                    : "information"
                                }`
                              ),
                          }
                        : {
                            tooltipText: t(
                              "benefits-section.nursery-benefit.nursery-tooltip"
                            ),

                            showDisableFlexMessage:
                              showFlexWarning(country) ||
                              selectedTab.disabledFlex["nursery-benefit"],
                            handleActiveToggle: () =>
                              toggleActive(selectedTab.id, "nurseryBenefit"),
                            handleAdvance: () =>
                              configureNurseryBenefit({
                                benefit: selectedTab,
                                country,
                              }),
                          };
                    return (
                      <BenefitCard
                        benefit={BenefitTypeEnum.NURSERY_BENEFIT}
                        title={t("benefits-section.nursery-benefit.nursery")}
                        tooltipText={t(
                          "benefits-section.nursery-benefit.nursery-tooltip"
                        )}
                        advanced={
                          treatments[featureFlagsDictionary.BI_264]
                            .treatment !== "on"
                        }
                        active={selectedTab.nurseryBenefit}
                        image={
                          selectedTab.nurseryBenefit
                            ? nurseryBenefitImageV2
                            : nurseryBenefitDisabledImageV2
                        }
                        country={country}
                        {...nurseryProps}
                      />
                    );
                  }}
                </SplitTreatments>
                <SplitTreatments
                  names={[featureFlagsDictionary.BI_264]}
                  attributes={{
                    companyId,
                    countryISOCode: country,
                    benefit: "training",
                  }}
                >
                  {({ treatments }) => {
                    const isActiveTreatment =
                      treatments[featureFlagsDictionary.BI_264].treatment ===
                      "on";

                    const trainingProps = {
                      tooltipText: t(
                        "benefits-section.education-benefit.education-tooltip"
                      ),

                      showDisableFlexMessage:
                        showFlexWarning(country) ||
                        selectedTab.disabledFlex["education-benefit"],
                      handleActiveToggle: () =>
                        toggleActive(selectedTab.id, "educationBenefit"),
                    };
                    if (!isActiveTreatment) {
                      return (
                        <BenefitCard
                          benefit={BenefitTypeEnum.EDUCATION_BENEFIT}
                          title={t(
                            "benefits-section.education-benefit.education"
                          )}
                          tooltipText={t(
                            "benefits-section.education-benefit.education-tooltip"
                          )}
                          active={selectedTab.educationBenefit}
                          image={educationBenefitImageV2}
                          country={country}
                          {...trainingProps}
                        />
                      );
                    }
                    return <></>;
                  }}
                </SplitTreatments>
                {isCompanyDemo && (
                  <LifeInsuranceTab
                    selectedTab={selectedTab}
                    companyId={companyId}
                    country={country}
                    toggleActive={toggleActive}
                  />
                )}
                {isCompanyDemo && (
                  <BenefitCard
                    benefit={BenefitTypeEnum.HEALTH_INSURANCE_BENEFIT}
                    title={t(
                      "benefits-section.accident-insurance-benefit.accident-insurance"
                    )}
                    active={accident}
                    image={accidentImg}
                    handleActiveToggle={() =>
                      setState((prev) => ({ ...prev, accident: !accident }))
                    }
                    country={country}
                  />
                )}
                {isCompanyDemo && (
                  <RetirementTab
                    selectedTab={selectedTab}
                    country={country}
                    companyId={companyId}
                    toggleActive={toggleActive}
                  />
                )}
                <SplitTreatments
                  names={[featureFlagsDictionary.BI_264]}
                  attributes={{
                    companyId,
                    countryISOCode: country,
                    benefit: "discount",
                  }}
                >
                  {({ treatments, isReady }) => {
                    const isActiveTreatment =
                      treatments[featureFlagsDictionary.BI_264].treatment ===
                      "on";

                    const discountProps =
                      isReady && isActiveTreatment
                        ? {
                            hasV2Screen: true,
                            handleSeeMore: () =>
                              redirectTo(
                                `/v2/benefits/compensation-plan/${
                                  selectedTab.id
                                }/discount?tab=${
                                  selectedTab.giftCardBenefit
                                    ? "settings"
                                    : "information"
                                }`
                              ),
                          }
                        : {
                            tooltipText: t(
                              "benefits-section.gift-cards-benefit.gift-cards-tooltip"
                            ),

                            showDisableFlexMessage:
                              showFlexWarning(country) ||
                              selectedTab.disabledFlex["gift-card-benefit"],
                            handleActiveToggle: () =>
                              toggleActive(selectedTab.id, "giftCardBenefit"),
                          };
                    return (
                      <BenefitCard
                        benefit={BenefitTypeEnum.GIFT_CARD_BENEFIT}
                        title={t(
                          "benefits-section.gift-cards-benefit.gift-cards"
                        )}
                        image={
                          selectedTab.giftCardBenefit
                            ? couponsBenefitImageV2
                            : couponsBenefitDisabledImageV2
                        }
                        active={selectedTab.giftCardBenefit}
                        advanced={!isActiveTreatment}
                        handleAdvance={() =>
                          displayGiftCardModal(selectedTab.id)
                        }
                        country={country}
                        {...discountProps}
                      />
                    );
                  }}
                </SplitTreatments>

                {hasRenting && (
                  <RentingTab
                    selectedTab={selectedTab}
                    companyId={companyId}
                    country={country}
                    toggleActive={toggleActive}
                  />
                )}
                {hasLifeInsurance && (
                  <LifeInsuranceTab
                    selectedTab={selectedTab}
                    companyId={companyId}
                    country={country}
                    toggleActive={toggleActive}
                  />
                )}
                {retirementInsuranceIsEnabledOnCompany && (
                  <RetirementTab
                    selectedTab={selectedTab}
                    country={country}
                    companyId={companyId}
                    toggleActive={toggleActive}
                  />
                )}
                {!benefitsV2.find(
                  (benefit) =>
                    benefit.benefit === BenefitCategory.HEALTH_INSURANCE
                ) && (
                  <HealthInsuranceCard
                    insuranceProducts={insuranceProducts}
                    country={country}
                    toggleActive={toggleActive}
                    selectedEmployeeGroup={selectedTab}
                  />
                )}

                <SplitTreatments
                  names={[
                    featureFlagsDictionary.COB_1796,
                    featureFlagsDictionary.EG_398,
                  ]}
                  attributes={{ companyId, country }}
                >
                  {({ treatments, isReady }) => {
                    if (isReady) {
                      return (
                        <>
                          {treatments[featureFlagsDictionary.COB_1796]
                            .treatment === "on" && (
                            <BenefitCard
                              benefit={BenefitTypeEnum.WELLNESS_BENEFIT}
                              title={`ifeel - ${t(
                                "common-messages.psychologists"
                              )}`}
                              image={
                                selectedTab.wellnessBenefit
                                  ? wellnessBenefitImageV2
                                  : wellnessBenefitDisabledImageV2
                              }
                              active={selectedTab.wellnessBenefit}
                              tooltipText={t(
                                "benefits-section.wellness-benefit.benefit-tab-tooltip"
                              )}
                              handleActiveToggle={() =>
                                toggleActive(selectedTab.id, "wellnessBenefit")
                              }
                              requestInfo={
                                selectedTab.wellnessConfig.length === 0
                              }
                              handleAdvance={() =>
                                configIFeelBenefit({
                                  companyId,
                                  modelId: selectedTab.id,
                                  wellnessConfig: selectedTab.wellnessConfig,
                                })
                              }
                              country={country}
                            />
                          )}

                          {pensionPlanIsEnabledOnCompany && (
                            <PensionPlanTab
                              country={country}
                              selectedTab={selectedTab}
                              companyId={companyId}
                              toggleActive={toggleActive}
                            />
                          )}

                          {treatments[featureFlagsDictionary.EG_398]
                            .treatment === "on" && (
                            <BenefitGym
                              companyGymBenefit={companyGymBenefit}
                              modelGymBenefit={selectedTab.gymBenefit}
                              country={country}
                              modelId={selectedTab.id}
                            />
                          )}
                        </>
                      );
                    }
                    return null;
                  }}
                </SplitTreatments>
              </>
            )}

            {benefitsV2.map((benefitV2) => (
              <SplitTreatments
                key={benefitV2.benefit}
                names={[featureFlagsDictionary.BI_264]}
                attributes={{
                  companyId,
                  countryISOCode: country,
                  benefit: benefitV2.benefit,
                }}
              >
                {({ treatments, isReady }) => {
                  if (!isReady) return null;
                  let benefitProps = {
                    benefit: benefitV2.benefit,
                    hasV2Screen: true,
                    tags:
                      benefitV2.state !== BenefitStates.ACTIVE
                        ? benefitV2.tags
                        : null,
                    handleSeeMore: async () => {
                      await analytics.track(events.benefitCard.name, {
                        type: benefitV2.type,
                        name: benefitV2.benefit,
                        id: benefitV2.benefit,
                        state: benefitV2.state,
                      });
                      redirectTo(
                        `/v2/benefits/compensation-plan/${selectedTab.id}/${
                          benefitV2.benefit
                        }?tab=${
                          benefitV2.state === BenefitStates.ACTIVE
                            ? "settings"
                            : "information"
                        }`
                      );
                    },
                    active: benefitV2.state === BenefitStates.ACTIVE,
                    country,
                    image: buildBenefitImage(
                      `benefit-icons/${benefitV2.benefit}-benefit.png`
                    ),
                    title: t(
                      alternateCountryTranslations(
                        country,
                        `v2.benefits.${benefitV2.benefit}.name`
                      )
                    ),
                  };

                  if (benefitV2.type !== BenefitType.CUSTOM) {
                    const isActiveTreatment =
                      treatments[featureFlagsDictionary.BI_264].treatment ===
                      "on";

                    if (!isActiveTreatment) {
                      return null;
                    }
                  }

                  if (benefitV2.type === BenefitType.CUSTOM) {
                    benefitProps = getCustomBenefitProps({
                      benefitProps,
                      benefit: benefitV2,
                      selectedTab,
                    });
                  }

                  return <BenefitCard {...benefitProps} />;
                }}
              </SplitTreatments>
            ))}
          </Row>
        </div>
      </TabPane>
    </TabContent>
  );
};

BenefitsTabs.defaultProps = {
  hasRenting: false,
  hasLifeInsurance: false,
  country: "ES",
  companyGymBenefit: undefined,
  benefitsV2: [],
};

BenefitsTabs.propTypes = {
  selectedTab: PropTypes.object.isRequired,
  insuranceProducts: PropTypes.array.isRequired,
  toggleActive: PropTypes.func.isRequired,
  disableAccumulation: PropTypes.func.isRequired,
  hasRenting: PropTypes.bool,
  hasLifeInsurance: PropTypes.bool,
  country: PropTypes.string,
  v2BenefitsConfig: PropTypes.object.isRequired,
  companyGymBenefit: PropTypes.object,
  benefitsV2: PropTypes.arrayOf(PropTypes.object),
};

export default withSplitFactory(sdkConfig)(BenefitsTabs);
